import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SemesterData, Course, PathwayData, Requirement } from '../types';
import './SemesterPage.css';

// Utility function to extract course code (subject + number) from course name
const extractCourseCode = (courseName: string): string => {
  const match = courseName.match(/^([A-Z]{2,6})\s+(\d+[A-Z]?)/);
  const courseCode = match ? `${match[1]} ${match[2]}` : courseName;
  // console.log(`Extracted "${courseCode}" from "${courseName}"`); // Debug log
  return courseCode;
};

const SemesterPage: React.FC = () => {
  const navigate = useNavigate();
  const [semesterData, setSemesterData] = useState<SemesterData | null>(null);
  const [pathwayData, setPathwayData] = useState<PathwayData | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [draggedCourse, setDraggedCourse] = useState<Course | null>(null);
  const [placedCourses, setPlacedCourses] = useState<Set<string>>(new Set());
  const [hideZeroCreditCourses, setHideZeroCreditCourses] = useState(false);
  const [currentSemester, setCurrentSemester] = useState<string>('');

  // Helper function to determine if a semester is past, current, or future
  const getSemesterStatus = (yearStr: string, semesterName: string): 'past' | 'current' | 'future' => {
    if (!currentSemester) return 'future'; // Default to future if current semester not set yet
    
    // semesterName is already the full key like "2024 Fall"
    const semesterKey = semesterName;
    
    if (semesterKey === currentSemester) return 'current';
    
    // Parse semester keys
    const [currentYear, currentTerm] = currentSemester.split(' ');
    const [semYear, semTerm] = semesterKey.split(' ');
    
    const currentYearNum = parseInt(currentYear);
    const semYearNum = parseInt(semYear);
    
    // Convert to academic progression order
    const getAcademicOrder = (year: number, term: string) => {
      const baseOrder = { 'Fall': 1, 'Winter': 2, 'Spring': 3, 'Summer': 4 };
      const termOrder = baseOrder[term as keyof typeof baseOrder] || 0;
      
      // For Spring/Summer, they belong to the academic year that started the previous calendar year
      if (term === 'Spring' || term === 'Summer') {
        return (year - 1) * 10 + termOrder;
      } else {
        // For Fall/Winter, they belong to the academic year starting in that calendar year
        return year * 10 + termOrder;
      }
    };
    
    const currentOrder = getAcademicOrder(currentYearNum, currentTerm);
    const semOrder = getAcademicOrder(semYearNum, semTerm);
    
    if (semOrder < currentOrder) return 'past';
    if (semOrder > currentOrder) return 'future';
    
    return 'current'; // Should not happen, but just in case
  };

  // Generate semester options for dropdown
  const generateSemesterOptions = () => {
    const options: string[] = [];
    
    if (semesterData) {
      semesterData.years.forEach(year => {
        // The semester keys in the data already include the year (e.g., "2024 Fall")
        Object.keys(year.semesters).forEach(semesterKey => {
          options.push(semesterKey);
        });
      });
    }
    
    // Sort options by academic year progression: Fall, Winter (same year), Spring, Summer (next year)
    return options.sort((a, b) => {
      const [yearA, termA] = a.split(' ');
      const [yearB, termB] = b.split(' ');
      
      const yearNumA = parseInt(yearA);
      const yearNumB = parseInt(yearB);
      
      // Convert to academic progression order
      // Fall/Winter use the year as-is, Spring/Summer are actually from previous academic year
      const getAcademicOrder = (year: number, term: string) => {
        const baseOrder = { 'Fall': 1, 'Winter': 2, 'Spring': 3, 'Summer': 4 };
        const termOrder = baseOrder[term as keyof typeof baseOrder] || 0;
        
        // For Spring/Summer, they belong to the academic year that started the previous calendar year
        if (term === 'Spring' || term === 'Summer') {
          return (year - 1) * 10 + termOrder;
        } else {
          // For Fall/Winter, they belong to the academic year starting in that calendar year
          return year * 10 + termOrder;
        }
      };
      
      return getAcademicOrder(yearNumA, termA) - getAcademicOrder(yearNumB, termB);
    });
  };

  useEffect(() => {
    // Load both semester and pathway data
    Promise.all([
      fetch('/assets/demo-semester.json').then(response => response.json()),
      fetch('/assets/demo.json').then(response => response.json())
    ])
      .then(([semesterData, pathwayData]: [SemesterData, PathwayData]) => {
        setSemesterData(semesterData);
        setPathwayData(pathwayData);
        
        // Extract all courses for the course list
        const allCourses: Course[] = [];
        
        // Add courses from pathway requirements
        pathwayData.requirementGroups.forEach(group => {
          group.requirements.forEach(requirement => {
            requirement.courses.forEach(course => {
              if (!allCourses.find(c => extractCourseCode(c.name) === extractCourseCode(course.name))) {
                allCourses.push(course);
              }
            });
          });
        });
        
        // Also add courses from semester data that might not be in requirements
        semesterData.years.forEach(year => {
          Object.values(year.semesters).forEach(semesterCourses => {
            semesterCourses.forEach(course => {
              if (!allCourses.find(c => extractCourseCode(c.name) === extractCourseCode(course.name))) {
                allCourses.push(course);
              }
            });
          });
        });
        
        setCourses(allCourses);

        // Track courses that are already placed in semesters
        const placed = new Set<string>();
        semesterData.years.forEach(year => {
          Object.values(year.semesters).forEach(semesterCourses => {
            semesterCourses.forEach(course => {
              placed.add(extractCourseCode(course.name));
            });
          });
        });
        setPlacedCourses(placed);
        
        // Update pathway data course statuses based on semester placement
        const updatedPathwayData = { ...pathwayData };
        updatedPathwayData.requirementGroups = updatedPathwayData.requirementGroups.map(group => ({
          ...group,
          requirements: group.requirements.map(req => ({
            ...req,
            courses: req.courses.map(course => {
              // Check if this course (by course code) is already placed in any semester
              const courseCode = extractCourseCode(course.name);
              const isPlacedInSemester = placed.has(courseCode);
              
              if (isPlacedInSemester) {
                return { ...course, status: 'Taken' };
              }
              return course;
            })
          }))
        }));
        setPathwayData(updatedPathwayData);
        
        // Set default current semester to the first available semester
        if (semesterData.years.length > 0) {
          const firstYear = semesterData.years[0];
          const firstSemesterKey = Object.keys(firstYear.semesters)[0];
          
          if (firstSemesterKey) {
            setCurrentSemester(firstSemesterKey);
          }
        }
        
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading data:', error);
        setLoading(false);
      });
  }, []);

  const getCourseClass = (course: Course, isPlaced: boolean = false, semesterStatus?: 'past' | 'current' | 'future', isInCourseList: boolean = false): string => {
    let baseClass = '';
    
    // If course is in the course list (not in semester), always show purple
    if (isInCourseList) {
      baseClass = 'course-not-finished';
    } else if (semesterStatus === 'past') {
      baseClass = 'course-past';
    } else if (semesterStatus === 'current') {
      baseClass = 'course-current';
    } else if (semesterStatus === 'future') {
      baseClass = 'course-future';
    } else {
      // For courses in semesters: taken (green) or not taken (purple)
      if (course.status === 'Taken') {
        baseClass = 'course-taken';
      } else {
        baseClass = 'course-not-finished';
      }
    }
    
    if (isPlaced) baseClass += ' course-placed';
    return baseClass;
  };

  const getRequirementClass = (requirement: Requirement): string => {
    // Check if all courses under this requirement are fulfilled
    const allCoursesFulfilled = requirement.courses.every((course: Course) => 
      course.status === 'Taken' || course.status === 1
    );
    
    // Only two states: done (green) or not finished (purple)
    if (allCoursesFulfilled) {
      return 'requirement-done';
    } else {
      return 'requirement-not-finished';
    }
  };

  const getSemesterClass = (semesterName: string): string => {
    if (semesterName.includes('Fall') || semesterName.includes('Spring')) {
      return 'semester-fall-spring';
    }
    return 'semester-winter-summer';
  };

  const loadCourses = (requirement: any) => {
    // Find the requirement in the current pathwayData to get updated course statuses
    if (pathwayData) {
      for (const group of pathwayData.requirementGroups) {
        const foundReq = group.requirements.find(req => req.name === requirement.name);
        if (foundReq) {
          setCourses(foundReq.courses);
          return;
        }
      }
    }
    // Fallback to original data if not found
    setCourses(requirement.courses);
  };

  const exportYearList = () => {
    console.log('Exporting year list...');
    // Add export functionality here
  };

  const addNewYear = () => {
    if (!semesterData) return;
    
    // Find the highest year number and add 1
    const currentYears = semesterData.years.map(year => {
      const yearMatch = year.year.match(/(\d{4})-(\d{2})/);
      return yearMatch ? parseInt(yearMatch[1]) : 2024;
    });
    
    const maxYear = Math.max(...currentYears);
    const newStartYear = maxYear + 1;
    const newEndYear = newStartYear + 1;
    const newYearStr = `${newStartYear}-${newEndYear.toString().slice(-2)}`;
    
    // Create new year with empty semesters using the correct format
    // Fall and Winter use the first year, Spring and Summer use the second year
    const newYear = {
      year: newYearStr,
      semesters: {
        [`${newStartYear} Fall`]: [],
        [`${newStartYear} Winter`]: [],
        [`${newEndYear} Spring`]: [],
        [`${newEndYear} Summer`]: []
      }
    };
    
    setSemesterData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        years: [...prev.years, newYear]
      };
    });
  };

  // Drag and Drop handlers
  const handleDragStart = (e: React.DragEvent, course: Course, fromSemester?: { year: string, semester: string }) => {
    // Check if the semester is in the past - if so, prevent dragging
    if (fromSemester) {
      const status = getSemesterStatus(fromSemester.year, fromSemester.semester);
      if (status === 'past') {
        e.preventDefault();
        return;
      }
    }
    
    setDraggedCourse(course);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', course.name);
    
    // Store source location if dragging from a semester
    if (fromSemester) {
      e.dataTransfer.setData('sourceYear', fromSemester.year);
      e.dataTransfer.setData('sourceSemester', fromSemester.semester);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, yearName: string, semesterName: string) => {
    e.preventDefault();
    
    if (!draggedCourse) return;

    // Check if the target semester is in the past - if so, prevent dropping
    const targetStatus = getSemesterStatus(yearName, semesterName);
    if (targetStatus === 'past') {
      setDraggedCourse(null);
      return;
    }

    const sourceYear = e.dataTransfer.getData('sourceYear');
    const sourceSemester = e.dataTransfer.getData('sourceSemester');

    // Update semester data
    setSemesterData(prev => {
      if (!prev) return prev;
      
      const newData = { ...prev };
      
      // Remove from source semester if moving between semesters
      if (sourceYear && sourceSemester) {
        newData.years = newData.years.map(year => {
          if (year.year === sourceYear) {
            return {
              ...year,
              semesters: {
                ...year.semesters,
                [sourceSemester]: year.semesters[sourceSemester].filter(c => 
                  extractCourseCode(c.name) !== extractCourseCode(draggedCourse.name)
                )
              }
            };
          }
          return year;
        });
      }

      // Add to target semester
      newData.years = newData.years.map(year => {
        if (year.year === yearName) {
          // Check if course already exists in this semester
          const courseExists = year.semesters[semesterName].find(c => 
            extractCourseCode(c.name) === extractCourseCode(draggedCourse.name)
          );
          if (!courseExists) {
            return {
              ...year,
              semesters: {
                ...year.semesters,
                [semesterName]: [...year.semesters[semesterName], draggedCourse]
              }
            };
          }
        }
        return year;
      });
      
      return newData;
    });

    // Mark course as placed (or keep it placed if moving between semesters)
    if (!sourceYear) {
      setPlacedCourses(prev => {
        const newSet = new Set(prev);
        newSet.add(extractCourseCode(draggedCourse.name));
        return newSet;
      });
    }

    // Update course status in pathway data to "Taken" when placed in a semester
    setPathwayData(prev => {
      if (!prev) return prev;
      
      const newPathwayData = { ...prev };
      newPathwayData.requirementGroups = newPathwayData.requirementGroups.map(group => ({
        ...group,
        requirements: group.requirements.map(req => ({
          ...req,
          courses: req.courses.map(course => {
            if (extractCourseCode(course.name) === extractCourseCode(draggedCourse.name)) {
              return {
                ...course,
                status: 'Taken'
              };
            }
            return course;
          })
        }))
      }));
      
      return newPathwayData;
    });
    
    setDraggedCourse(null);
  };

  // Handle dropping outside of any semester (to remove from semesters)
  const handleDropOutside = (e: React.DragEvent) => {
    e.preventDefault();
    
    if (!draggedCourse) return;

    const sourceYear = e.dataTransfer.getData('sourceYear');
    const sourceSemester = e.dataTransfer.getData('sourceSemester');

    // Only allow removal if dragging from a semester
    if (sourceYear && sourceSemester) {
      // Remove from source semester
      setSemesterData(prev => {
        if (!prev) return prev;
        
        const newData = { ...prev };
        newData.years = newData.years.map(year => {
          if (year.year === sourceYear) {
            return {
              ...year,
              semesters: {
                ...year.semesters,
                [sourceSemester]: year.semesters[sourceSemester].filter(c => 
                  extractCourseCode(c.name) !== extractCourseCode(draggedCourse.name)
                )
              }
            };
          }
          return year;
        });
        return newData;
      });

      // Remove from placed courses (make it available again)
      setPlacedCourses(prev => {
        const newSet = new Set(prev);
        newSet.delete(extractCourseCode(draggedCourse.name));
        return newSet;
      });

      // Update course status back to original state when removed from semester
      setPathwayData(prev => {
        if (!prev) return prev;
        
        const newPathwayData = { ...prev };
        newPathwayData.requirementGroups = newPathwayData.requirementGroups.map(group => ({
          ...group,
          requirements: group.requirements.map(req => ({
            ...req,
            courses: req.courses.map(course => {
              if (extractCourseCode(course.name) === extractCourseCode(draggedCourse.name)) {
                return {
                  ...course,
                  status: 'Not Taken' // Simple "not taken" state (purple)
                };
              }
              return course;
            })
          }))
        }));
        
        return newPathwayData;
      });
    }
    
    setDraggedCourse(null);
  };

  const calculateSemesterCredits = (courses: Course[]): number => {
    return courses.reduce((total, course) => total + course.credit, 0);
  };

  const filteredCourses = courses.filter(course =>
    course.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (!hideZeroCreditCourses || course.credit > 0)
  );

  if (loading) {
    return <div className="loading">Loading semester data...</div>;
  }

  if (!semesterData || !pathwayData) {
    return <div className="error">Failed to load data</div>;
  }

  return (
    <div className="semester-page">
      {/* Navigation Bar */}
      <div className="navigation-bar">
        <div className="nav-left">
          <button onClick={() => navigate('/planning')} className="nav-link-button">
            ← Course Planning
          </button>
          <button onClick={() => navigate('/login')} className="nav-link-button">
            ← Login
          </button>
        </div>
        <div className="nav-center">
          <h1 className="title">Semester Plan</h1>
        </div>
        <div className="nav-right">
          <button onClick={exportYearList} className="export-button">
            Export Year List
          </button>
        </div>
      </div>

      {/* Options Bar */}
      <div className="options-bar">
        <div className="options-left">
          <label className="checkbox-container">
            <input 
              type="checkbox" 
              checked={hideZeroCreditCourses}
              onChange={(e) => setHideZeroCreditCourses(e.target.checked)}
            />
            <span className="checkmark"></span>
            Hide 0-credit courses
          </label>
        </div>
        <div className="options-right">
          <label className="dropdown-label">
            Current Semester:
            <select 
              className="semester-dropdown"
              value={currentSemester}
              onChange={(e) => setCurrentSemester(e.target.value)}
            >
              {generateSemesterOptions().map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div 
        className="content-area"
        onDragOver={handleDragOver}
        onDrop={handleDropOutside}
      >
        <div className="semester-calendar">
          {semesterData.years.map((year, yearIndex) => (
            <div key={yearIndex} className="year-section">
              <div className="year-title">{year.year}</div>
              <div className="semesters-container">
                {Object.entries(year.semesters).map(([semesterName, semesterCourses]) => (
                  <div key={semesterName} className={`semester-section ${getSemesterClass(semesterName)}`}>
                    <div className="semester-header">
                      <div className="semester-title">{semesterName}</div>
                      <div className="semester-credits">
                        {calculateSemesterCredits(semesterCourses)}
                      </div>
                    </div>
                    <div 
                      className="semester-basket"
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, year.year, semesterName)}
                    >
                      {semesterCourses.length === 0 ? (
                        <div className="empty-placeholder">Drop courses here</div>
                      ) : (
                        semesterCourses
                          .filter(course => !hideZeroCreditCourses || course.credit > 0)
                          .map((course, courseIndex) => (
                          <div 
                            key={courseIndex} 
                            className={`course-block ${getCourseClass(course, false, getSemesterStatus(year.year, semesterName))}`}
                            draggable={getSemesterStatus(year.year, semesterName) !== 'past'}
                            onDragStart={(e) => handleDragStart(e, course, { year: year.year, semester: semesterName })}
                          >
                            {course.name}
                            <div className="course-credits">{course.credit}cr</div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          {/* Add Year Button */}
          <div className="add-year-section">
            <button onClick={addNewYear} className="add-year-button">
              + Add Year
            </button>
          </div>
        </div>
        
        <div className="course-list">
          <div className="search-bar">
            <input 
              type="text" 
              placeholder="Search courses..." 
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <img src="/assets/search-icon.png" alt="Search" className="search-icon" />
          </div>
          {filteredCourses.map((course, courseIndex) => (
            <div 
              key={courseIndex} 
              className={`course-block ${getCourseClass(course, placedCourses.has(extractCourseCode(course.name)), undefined, true)}`}
              draggable={!placedCourses.has(extractCourseCode(course.name))}
              onDragStart={(e) => handleDragStart(e, course)}
              style={{
                opacity: placedCourses.has(extractCourseCode(course.name)) ? 0.3 : 1,
                cursor: placedCourses.has(extractCourseCode(course.name)) ? 'not-allowed' : 'move'
              }}
            >
              {course.name}
              <div className="course-credits">{course.credit}cr</div>
            </div>
          ))}
        </div>
        
        <div className="requirement-list">
          {pathwayData.requirementGroups.length === 0 ? (
            <div>No requirements loaded</div>
          ) : (
            pathwayData.requirementGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="requirement-group">
                <div className="group-title">{group.name}</div>
                <div className="requirements">
                  {group.requirements.map((req, reqIndex) => (
                    <button 
                      key={reqIndex} 
                      className={`requirement-button ${getRequirementClass(req)}`}
                      onClick={() => loadCourses(req)}
                    >
                      {req.name}
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default SemesterPage;
